import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import { PhoneInput } from '../components/PhoneInput';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorCode, saveErrorText } from '../utils/apiError';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout, Card, TermsAcceptanceHistory } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import Modal from '../components/Modal';
import { CustomerBanks } from '../components/CustomerBanks';
import { customerApi, adminApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import type { Customer } from '../types';

interface CustomerFormData {
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  notes: string;
}

export function CustomerView() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [companyId, setCompanyId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [formData, setFormData] = useState<CustomerFormData>({
    phone: '',
    address: '',
    postalCode: '',
    city: '',
    notes: ''
  });

  const basePath = user?.isAdmin ? '/admin/customers' : '/customers';

  useEffect(() => {
    if (id) {
      loadCustomer(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCustomer = async (customerId: string) => {
    try {
      setIsLoading(true);
      let data: Customer | undefined;
      if (user?.isAdmin) {
        // Admin has no company context, so the company-scoped endpoint is not
        // available. The admin endpoint returns the record with its company,
        // whose id is needed for saving (ClickUp 869fjkyz1: no list lookup).
        const record = await adminApi.getCustomer(customerId);
        data = record;
        setCompanyId(record.companyId ?? '');
      } else {
        data = await customerApi.getCustomer(customerId);
      }
      if (!data) {
        setMessage({ type: 'error', text: t('common.error') });
        return;
      }
      setCustomer(data);
      setFormData({
        phone: data.phone || '',
        address: data.address || '',
        postalCode: data.postalCode || '',
        city: data.city || '',
        notes: data.notes || ''
      });
    } catch (err) {
      setMessage({ type: 'error', text: t('common.error') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage(null);
  };

  const handleCancelEdit = () => {
    if (customer) {
      setFormData({
        phone: customer.phone || '',
        address: customer.address || '',
        postalCode: customer.postalCode || '',
        city: customer.city || '',
        notes: customer.notes || ''
      });
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!id || !customer) return;
    try {
      setIsSaving(true);
      const updated = user?.isAdmin
        ? await adminApi.updateCustomer(id, {
            name: customer.name,
            email: customer.email,
            phone: formData.phone,
            address: formData.address,
            postalCode: formData.postalCode,
            city: formData.city,
            notes: formData.notes,
            companyId
          })
        : await customerApi.updateCustomer(id, {
            email: customer.email,
            name: customer.name,
            phone: formData.phone,
            address: formData.address,
            postalCode: formData.postalCode,
            city: formData.city,
            notes: formData.notes
          });
      // The banks belong to the person and are not part of this save.
      setCustomer({ ...updated, banks: updated.banks ?? customer.banks });
      setIsEditing(false);
      setMessage({ type: 'success', text: t('common.saved') });
    } catch (err) {
      setMessage({ type: 'error', text: saveErrorText(t, err, 'common.error') });
    } finally {
      setIsSaving(false);
    }
  };

  // The contractor removes the client, and with it the connection to the
  // client's account, e.g. one the client made by inviting (ClickUp 869fj4t2h).
  const handleRemove = async () => {
    if (!id) return;
    try {
      setIsRemoving(true);
      await customerApi.deleteCustomer(id);
      navigate(basePath);
    } catch (err) {
      setConfirmRemove(false);
      setMessage({
        type: 'error',
        text: apiErrorCode(err) === 'CUSTOMER_HAS_PROJECTS' ? t('customerView.remove.hasProjects') : t('customerView.remove.error')
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const handleChange = (field: keyof CustomerFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!customer) {
    return (
      <DashboardLayout>
        <div className="alert alert-danger">{t('common.error')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {t('customerView.title')} - {customer.customerNumber}
            </h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('nav.customers'), path: basePath },
            { label: customer.name }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('customerView.description')}</p>
        </div>
        <div className="d-flex justify-content-end mt-2">
          <button className="btn btn-light-brand" onClick={() => navigate(basePath)}>
            <i className="feather-arrow-left me-2"></i>
            {t('common.back')}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="mb-4" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <ul className="nav nav-tabs nav-tabs-custom-style flex-nowrap" role="tablist" style={{ minWidth: 'max-content' }}>
          <li className="nav-item" role="presentation">
            <button className="nav-link px-3 active">
              <i className="feather-user d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('customerView.tab.overview')}</span>
            </button>
          </li>
        </ul>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        <div className="tab-pane fade show active">
          <Card>
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="card-title mb-0">{t('customerView.tab.overview')}</h5>
              {!isEditing && (
                <button type="button" className="btn btn-primary" onClick={handleEdit} aria-label={t('common.change', 'Ändra')}>
                  <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
                </button>
              )}
            </div>
            <div className="card-body">
              {user?.isAdmin && (
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('adminCustomers.columns.companyNumber')}</label>
                    <input type="text" className="form-control" value={(customer as any).companyNumber ?? ''} disabled />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('adminCustomers.columns.companyName')}</label>
                    <input type="text" className="form-control" value={(customer as any).companyName ?? ''} disabled />
                    {companyId && (customer as any).companyName && (
                      <Link to={`/admin/company-list/${companyId}`} className="small d-inline-block mt-1">
                        <i className="feather-external-link me-1"></i>
                        {t('adminCustomers.openCompany', 'Öppna hantverkarens adminvy')}
                      </Link>
                    )}
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('customers.columns.name')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={customer.name || ''}
                    disabled
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('customers.columns.email')}</label>
                  <input
                    type="email"
                    className="form-control"
                    value={customer.email || ''}
                    disabled
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('customers.columns.phone')}</label>
                  <PhoneInput name="phone"
                    type="text"
                    className="form-control"
                    value={isEditing ? formData.phone : (customer.phone || '')}
                    disabled={!isEditing}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <AddressAutocomplete
                    name="address"
                    label={t('customers.columns.address')}
                    onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                    type="text"
                    value={isEditing ? formData.address : (customer.address || '')}
                    disabled={!isEditing}
                    onChange={(e) => handleChange('address', e.target.value)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('customers.columns.postalCode')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={isEditing ? formData.postalCode : (customer.postalCode || '')}
                    disabled={!isEditing}
                    onChange={(e) => handleChange('postalCode', e.target.value)}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('customers.columns.city')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={isEditing ? formData.city : (customer.city || '')}
                    disabled={!isEditing}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                </div>
              </div>
              {customer.banks && (
                <div className="mb-3">
                  <CustomerBanks
                    banks={customer.banks}
                    personUserId={customer.personUserId}
                    editable={Boolean(user?.isAdmin)}
                    onSaved={banks => setCustomer(previous => (previous ? { ...previous, banks } : previous))}
                  />
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">{t('customers.columns.notes')}</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={isEditing ? formData.notes : (customer.notes || '')}
                  disabled={!isEditing}
                  onChange={(e) => handleChange('notes', e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end gap-2 mt-4">
                {isEditing ? (
                  <>
                    <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                      {t('common.cancel')}
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                      {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                      {t('common.save')}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Only a client without projects can be removed (ClickUp 869fj4t2h). */}
                    {!user?.isAdmin && customer?.hasProjects === false && (
                      <button type="button" className="btn btn-outline-danger me-auto customer-remove-action" onClick={() => setConfirmRemove(true)}>
                        <i className="feather-user-x me-2" aria-hidden="true"></i>
                        {t('customerView.remove.action')}
                      </button>
                    )}
                    <button className="btn btn-secondary" onClick={() => navigate(basePath)}>
                      {t('common.cancel')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </Card>
          {customer && <TermsAcceptanceHistory userId={customer.personUserId ?? null} />}
        </div>
      </div>

      {confirmRemove && customer && (
        <Modal
          title={t('customerView.remove.title')}
          onClose={() => setConfirmRemove(false)}
          closeAriaLabel={t('common.close')}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmRemove(false)} disabled={isRemoving}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={() => void handleRemove()} disabled={isRemoving} data-testid="customer-remove-confirm">
                {isRemoving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                {t('customerView.remove.confirm')}
              </button>
            </>
          }
        >
          <p className="mb-0">{t('customerView.remove.body', { name: customer.name })}</p>
        </Modal>
      )}
    </DashboardLayout>
  );
}
